import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import currentTimeZone from '../../../config/timeZone';
import i18n from '../../../i18n';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
// import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
// import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import { convertUTCtoTimezoneDate } from '../../../shared-functions/TimezoneConversions';
import tariffApi from '../../../utils/api/tariff';

// interface PriceComponentTableInterface {
//   id: string;
//   componentType: string;
//   componentName: string;
//   valueType: string;
//   value: string;
//   order: number;
// }

// interface TimeBasedPricingTableInterface {
//   id: number;
//   name: string;
//   startTime: string;
//   endTime: string;
//   recurrenceType: string;
//   date: string;
//   days: string;
//   priceMultiplier: string;
// }

interface ViewTariffProperties {
  id?: string;
  isSidePanel?: boolean;
}

/**
 * Convert UTC to date and time with seconds
 * @param {string} utcString - UTC string
 * @returns {string} Date and time with seconds
 */
const ConvertUTCtoDateTimeWithSeconds = (utcString: string | undefined | null): string => {
  if (!utcString) {
    return '';
  }
  const date = new Date(utcString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = {
    timeZone: currentTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  try {
    return new Intl.DateTimeFormat(i18n.language, options).format(date);
  } catch (error) {
    console.log('Error formatting date:', error);
    return '';
  }
};

/**
 * View tariff component
 * @param {any} properties - View tariff properties
 * @returns {any} View tariffs component
 */
function ViewTariff(properties: ViewTariffProperties = {}) {
  const { t } = useTranslation('tariff');
  const timeSlotView = true;
  const { id, isSidePanel } = properties;
  const [searchParameters] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: parameterId } = useParams<{ id: string }>();
  const tariffId = id || searchParameters.get('tariffId') || parameterId;
  const [tariffDetails, setTariffDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);

  // Calculator states
  const isPartner = searchParameters.get('isPartner') === 'true';
  const isExtended = searchParameters.get('isExtended') === 'true' || !isSidePanel;
  const [calculatorData, setCalculatorData] = useState<any>({
    powerConsumed: '',
    sessionStartDateTime: '',
    sessionEndDateTime: '',
  });
  const [calculating, setCalculating] = useState(false);
  const [calcResults, setCalcResults] = useState<any>({ totalAmount: '0.00' });

  // // Handle sidepanel resize when extended
  // useEffect(() => {
  //   let sidePanelElement: HTMLElement | null = null;
  //   let tableElement: HTMLElement | null = null;
  //   // if (isSidePanel) {
  //   //   sidePanelElement = document.querySelector('.custom-table-side-panel') as HTMLElement;
  //   //   tableElement = document.querySelector('.custom-table') as HTMLElement;
  //   //   if (sidePanelElement && tableElement) {
  //   //     if (isExtended) {
  //   //       sidePanelElement.style.setProperty('width', '70%', 'important');
  //   //       tableElement.style.setProperty('width', '30%', 'important');
  //   //     } else {
  //   //       sidePanelElement.style.setProperty('width', '50%', 'important');
  //   //       tableElement.style.setProperty('width', '50%', 'important');
  //   //     }
  //   //   }
  //   // }
  //   return () => {
  //     if (sidePanelElement && tableElement) {
  //       sidePanelElement.style.removeProperty('width');
  //       tableElement.style.removeProperty('width');
  //     }
  //   };
  // }, [isExtended, isSidePanel]);

  /**
   * Update calculator data
   * @param {string} field - Field to update
   * @param {any} value - New value
   */
  const updateCalculatorData = (field: string, value: string | number) => {
    setCalculatorData((previous: any) => ({ ...previous, [field]: value }));
  };

  /**
   * Handle calculate tariff based on power consumed and session start time
   */
  const handleCalculate = () => {
    const requestData = {
      startDateUTC: calculatorData.sessionStartDateTime
        ? new Date(calculatorData.sessionStartDateTime).toISOString()
        : '',
      //  endDateUTC: calculatorData.sessionEndDateTime
      //    ? new Date(calculatorData.sessionEndDateTime).toISOString()
      //    : '',
      unitsConsumed: Number(calculatorData.powerConsumed),
    };
    if (typeof tariffId === 'string') {
      setCalculating(true);
      tariffApi
        .fetchSingerTariffCalculatorData(tariffId, requestData, isPartner)
        .then((response) => {
          setCalculating(false);
          const data = response.data || {};

          data.priceBreakdown = response?.data?.amountSplit?.map((split: any) => ({
            componentName: split.componentName,
            amount: `${split.contributedAmount.toFixed(2)} ${data.currency}`,
          })) || [
            { componentName: 'Base Price', amount: '0.00 SAR' },
            { componentName: 'Operating Cost', amount: '0.00 SAR' },
          ];
          if (response.data.timeBasedPricing.slotName) {
            data.timeBasePrice = [
              {
                componentName: response?.data?.timeBasedPricing?.slotName || t('additionalCharges'),
                amount: `${response?.data?.timeBasedPricing?.additionalAmount?.toFixed(2)} ${data.currency}`,
              },
            ];
          }
          data.totalAmount =
            `${response?.data?.totalAmount?.toFixed(2)} ${data.currency}` || '0.00 SAR';
          setCalcResults(data);
        })
        .catch((error: any) => {
          setCalculating(false);
          dispatch(
            setNotificationMessage({
              status: true,
              message:
                error?.response?.data?.message ||
                t('invalidTariffDetails'),
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  useEffect(() => {
    if (tariffId) {
      setLoading(true);
      tariffApi
        .fetchSingleTariff(tariffId, isPartner)
        .then((response) => {
          const rawScopeLevels = response?.data?.scopeLevels;
          let parsedScopeLevels: any[] = [];
          if (Array.isArray(rawScopeLevels)) {
            parsedScopeLevels = rawScopeLevels;
          } else if (typeof rawScopeLevels === 'string') {
            try {
              parsedScopeLevels = JSON.parse(rawScopeLevels);
            } catch {
              parsedScopeLevels = [];
            }
          }

          const output = {
            // status: response?.data?.isActive,
            status: response?.data?.status,
            enforcementDate: convertUTCtoTimezoneDate(
              response?.data?.effectiveFrom,
              response?.data?.timezone,
            ),
            expiryDate: convertUTCtoTimezoneDate(
              response?.data?.effectiveTo,
              response?.data?.timezone,
            ),
            tariffName: response?.data?.name,
            tariffType: response?.data?.type,
            currentType: response?.data?.currentType,
            accountCustomId: response?.data?.accountCustomId,
            connectorRating: `${response?.data?.connectorRating?.currentType} ${response?.data?.connectorRating?.capacityKW} ${response?.data?.connectorRating?.unit}`,
            scopeLevels: response?.data?.scopeLevels,
            country: parsedScopeLevels?.find((level: any) => level.scope === 'country')?.scopeName,
            state: parsedScopeLevels?.find((level: any) => level.scope === 'state')?.scopeName,
            city: parsedScopeLevels?.find((level: any) => level.scope === 'city')?.scopeName,
            stationId: response?.data?.stationId,
            accountName: response?.data?.accountCustomId,
            scope: response?.data?.scope,
            scopeId: response?.data?.scopeId,
            scopeName: response?.data?.scopeName,
            currency: response?.data?.currency,
            createdBy: ConvertUTCtoDateTimeWithSeconds(response?.data?.createdAt),
            editedBy: ConvertUTCtoDateTimeWithSeconds(response?.data?.updatedAt),
            priceComponents: response?.data?.tariffPricingComponent.map(
              (comp: any, index: number) => ({
                id: index + 1,
                componentType: comp.componentType,
                componentName: comp.componentName,
                valueType: comp.valueType,
                value: comp.value,
                order: comp.order,
              }),
            ),
            timeBasedPricingSlots: (
              response?.data?.timeBasedPricingDetails ||
              response?.data?.timeBasedPricingSlots ||
              []
            ).map((slot: any, index: number) => ({
              id: index + 1,
              name: slot.name,
              startTime: slot.startTime,
              endTime: slot.endTime,
              recurrenceType: slot.recurrenceType,
              applicableOn:
                slot.dates && slot.dates.length > 0
                  ? slot.dates.map((date: string) => ConvertDDMMYYYYtoISO(date)).join(', ')
                  : '-',
              days: Array.isArray(slot.daysOfWeek) ? slot.daysOfWeek.join(' | ') : '-',
              priceMultiplier: String(slot.priceMultiplier),
            })),
            isParkingFeeEnabled: response?.data?.isParkingFeeEnabled,
            parkingFeeDetails: response?.data?.parkingFeeDetails || null,
            isReservationFeeEnabled: response?.data?.isReservationFeeEnabled,
            reservationFeeDetails: response?.data?.reservationFeeDetails || null,
            taxCountry: response?.data?.taxCountry || null,
            taxComponents: response?.data?.taxComponents || [],
            taxApplied: !!response?.data?.taxApplied,
            taxStampedAt: ConvertUTCtoDateTimeWithSeconds(response?.data?.taxStampedAt),
          };
          setTariffDetails(output);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [tariffId, isPartner]);

  if (loading) {
    return (
      <div
        className={`view-tariff-wrapper ${isSidePanel ? 'sidepanel-mode' : ''} ${isExtended ? 'extended-mode' : ''}`}>
        {!isSidePanel && (
          <PageTitle
            title={t('tariffDetails')}
            topRightContent={
              <div className="page-title-button-group">
                <MyButton
                  label={t('cancel')}
                  buttonType="outline"
                  onClickFunc={() => navigate(-1)}
                />
              </div>
            }
          />
        )}
        <div className="view-tariff-card loader-card">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`view-tariff-wrapper ${isSidePanel ? 'sidepanel-mode' : ''} ${isExtended ? 'extended-mode' : ''}`}>
      {!isSidePanel && (
        <PageTitle
          title={t('tariffDetails')}
          topRightContent={
            <div className="page-title-button-group">
              <MyButton
                label={t('cancel')}
                buttonType="outline"
                onClickFunc={() => navigate(-1)}
              />
              {tariffDetails.status !== 'expired' && (
                <MyButton
                  label={t('editTariff')}
                  buttonType="submit"
                  onClickFunc={() =>
                    navigate(
                      `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
                    )
                  }
                />
              )}
            </div>
          }
        />
      )}

      <div className="view-tariff-card">
        <div className="extended-layout-container">
          <div className="details-left-column">
            {/* {isSidePanel && tariffDetails.tariffName && (
              <div className="tariff-hero-banner">
                <div className="hero-details">
                  <h3 className="tariff-name-title">{tariffDetails.tariffName}</h3>
                  {tariffDetails.tariffType && (
                    <span className="tariff-type-badge">{tariffDetails.tariffType}</span>
                  )}
                </div>
                {tariffDetails.status && (
                  <div className={`status-badge ${tariffDetails.status}`}>
                    <span className="status-dot" />
                    <span>{tariffDetails.status}</span>
                  </div>
                )}
              </div>
            )} */}
            <div className="details-section">
              <p className="section-title">{t('basicDetails')}</p>
              <div className="details-grid three-column">
                <div className="detail-item">
                  <p className="label">{t('tariffName')}</p>
                  <p className="value">{tariffDetails.tariffName || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('tariffType')}</p>
                  <p className="value">{tariffDetails.tariffType || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('country')}</p>
                  <p className="value">{tariffDetails.country || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('state')}</p>
                  <p className="value">{tariffDetails.state || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('city')}</p>
                  <p className="value">{tariffDetails.city || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('scopeId')}</p>
                  <p className="value">{tariffDetails.scopeId || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('scopeName')}</p>
                  <p className="value">{tariffDetails.scopeName || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('createdBy')}</p>
                  <p className="value">{tariffDetails.createdBy || 'N/A'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('editedBy')}</p>
                  <p className="value">{tariffDetails.editedBy || '-'}</p>
                </div>
              </div>
            </div>

            <div className="details-section">
              <p className="section-title">{t('technicalDetails')}</p>

              <div className="details-grid two-column">
                <div className="detail-item">
                  <p className="label">{t('currentType')}</p>
                  <p className="value">{tariffDetails.currentType || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('connectorRatingKw')}</p>
                  <p className="value">{tariffDetails.connectorRating || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('enforcementDate')}</p>
                  <p className="value">{tariffDetails.enforcementDate || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">{t('expiryDate')}</p>
                  <p className="value">{tariffDetails.expiryDate || '-'}</p>
                </div>
              </div>
            </div>

            <div className="details-section">
              <p className="section-title">{t('priceComponents')}</p>

              <div className="simple-table">
                <div className="simple-table-header">
                  <div className="col-1-5">{t('componentType')}</div>
                  <div className="col-1-5">{t('componentName')}</div>
                  <div className="col-1-5">{t('valueType')}</div>
                  <div className="col-1">{t('value')}</div>
                  <div className="col-0-5">{t('order')}</div>
                </div>
                {tariffDetails?.priceComponents?.map((comp: any) => (
                  <div className="simple-table-row" key={comp.id}>
                    <div className="col-1-5">{comp.componentType || '-'}</div>
                    <div className="col-1-5">{comp.componentName || '-'}</div>
                    <div className="col-1-5">{comp.valueType || '-'}</div>
                    <div className="col-1">
                      {comp.value !== undefined && comp.value !== null
                        ? `${Number(comp.value).toFixed(2)} ${tariffDetails.currency || ''}`
                        : '-'}
                    </div>
                    <div className="col-0-5">{comp.order ?? '-'}</div>
                  </div>
                ))}
                {(!tariffDetails?.priceComponents ||
                  tariffDetails.priceComponents.length === 0) && (
                  <div className="simple-table-row">
                    <div className="col-1">{t('noPriceComponentsFound')}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="details-section">
              <p className="section-title">{t('parkingFee')}</p>
              {tariffDetails.isParkingFeeEnabled && tariffDetails.parkingFeeDetails ? (
                <div className="details-grid three-column">
                  <div className="detail-item">
                    <p className="label">
                      {tariffDetails.currency
                        ? `${t('price')} (${tariffDetails.currency} / ${t('minutes')})`
                        : `${t('price')} (${t('minutes')})`}
                    </p>
                    <p className="value">
                      {tariffDetails.parkingFeeDetails.price !== undefined &&
                      tariffDetails.parkingFeeDetails.price !== null
                        ? `${Number(tariffDetails.parkingFeeDetails.price).toFixed(2)} ${tariffDetails.currency || ''}`
                        : '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('gracePeriodMinutes')}</p>
                    <p className="value">{tariffDetails.parkingFeeDetails.graceMinutes ?? '-'}</p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('minChargingDurationMinutes')}</p>
                    <p className="value">
                      {tariffDetails.parkingFeeDetails.minChargingDurationMinutes ?? '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('billingStepMinutes')}</p>
                    <p className="value">
                      {tariffDetails.parkingFeeDetails.billingStepSeconds !== undefined &&
                      tariffDetails.parkingFeeDetails.billingStepSeconds !== null
                        ? Number(tariffDetails.parkingFeeDetails.billingStepSeconds) / 60
                        : '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('maxCapOptional')}</p>
                    <p className="value">
                      {tariffDetails.parkingFeeDetails.maxCap !== undefined &&
                      tariffDetails.parkingFeeDetails.maxCap !== null &&
                      tariffDetails.parkingFeeDetails.maxCap !== ''
                        ? `${Number(tariffDetails.parkingFeeDetails.maxCap).toFixed(2)} ${tariffDetails.currency || ''}`
                        : '-'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="no-data-text">{t('parkingFeeNotEnabled')}</p>
              )}
            </div>

            <div className="details-section">
              <p className="section-title">{t('reservationFee')}</p>
              {tariffDetails.isReservationFeeEnabled && tariffDetails.reservationFeeDetails ? (
                <div className="details-grid three-column">
                  <div className="detail-item">
                    <p className="label">{t('reservationPriceType')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.reservationPrice?.priceType || '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('reservationPriceValue')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.reservationPrice?.value !== undefined
                        ? `${Number(tariffDetails.reservationFeeDetails.reservationPrice.value).toFixed(2)} ${tariffDetails.currency || ''}`
                        : '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('gracePeriodMinutes')}</p>
                    <p className="value">{tariffDetails.reservationFeeDetails.graceMinutes ?? '-'}</p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('maxHoldMinutes')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.maxHoldMinutes ?? '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('noShowChargeType')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.noShowCharge?.chargeType || '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('noShowChargeValue')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.noShowCharge?.value !== undefined
                        ? `${Number(tariffDetails.reservationFeeDetails.noShowCharge.value).toFixed(2)} ${tariffDetails.currency || ''}`
                        : '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('feeOnSuccessAction')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.feeOnSuccess?.actionType || '-'}
                    </p>
                  </div>
                  <div className="detail-item">
                    <p className="label">{t('expiryGraceMinutes')}</p>
                    <p className="value">
                      {tariffDetails.reservationFeeDetails.expiryGraceMinutes ?? '-'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="no-data-text">{t('reservationFeeNotEnabled')}</p>
              )}
            </div>

            <div className="details-section">
              <p className="section-title">{t('taxSectionTitle')}</p>
              <div className="details-grid three-column">
                <div className="detail-item">
                  <p className="label">{t('taxCountry')}</p>
                  <p className="value">{tariffDetails.taxCountry?.scopeName || '-'}</p>
                </div>
                <div className="detail-item">
                  <p className="label">{t('taxComponents')}</p>
                  <p className="value">
                    {tariffDetails.taxComponents && tariffDetails.taxComponents.length > 0
                      ? tariffDetails.taxComponents
                          .map((tax: any) => `${tax.name} — ${tax.percentage}%`)
                          .join(', ')
                      : '-'}
                  </p>
                </div>
                <div className="detail-item">
                  <p className="label">{t('taxApplied')}</p>
                  <p className="value">{tariffDetails.taxApplied ? t('yes') : t('no')}</p>
                </div>
                <div className="detail-item">
                  <p className="label">{t('taxStampedAt')}</p>
                  <p className="value">{tariffDetails.taxStampedAt || '-'}</p>
                </div>
              </div>
            </div>

            {timeSlotView && (
              <div className="details-section">
                <p className="section-title">{t('timeBasedPricingSlots')}</p>

                <div className="pricing-slots-list">
                  {!tariffDetails?.timeBasedPricingSlots ||
                  tariffDetails.timeBasedPricingSlots.length === 0 ? (
                    <div className="pricing-slot-item">
                      <div className="details-grid three-column">
                        <div className="detail-item">
                          <p className="label">{t('slotName')}</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">{t('startTime')}</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">{t('endTime')}</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">{t('recurrenceType')}</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">{t('applicableOn')}</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">{t('days')}</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">{t('multiplier')}</p>
                          <p className="value">-</p>
                        </div>
                      </div>
                      <div style={{ marginTop: '24px' }}>
                        <p className="no-data-text">{t('noTimeBasedPricingSlotsFound')}</p>
                      </div>
                    </div>
                  ) : (
                    tariffDetails.timeBasedPricingSlots.map((slot: any, index: number) => (
                      <div className="pricing-slot-item" key={slot.id}>
                        {index > 0 && <hr className="slot-divider" />}
                        <div className="details-grid three-column">
                          <div className="detail-item">
                            <p className="label">{t('slotName')}</p>
                            <p className="value">{slot.name || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">{t('startTime')}</p>
                            <p className="value">{slot.startTime || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">{t('endTime')}</p>
                            <p className="value">{slot.endTime || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">{t('recurrenceType')}</p>
                            <p className="value">{slot.recurrenceType || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">{t('applicableOn')}</p>
                            <p className="value">{slot.applicableOn || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">{t('days')}</p>
                            <p className="value">{slot.days || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">{t('multiplier')}</p>
                            <p className="value">{slot.priceMultiplier || '-'}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {isExtended && (
            <div className="calculator-right-column">
              <div className="calculator-panel">
                <h3 className="section-title">{t('calculateTariff')}</h3>

                <div className="form-field">
                  <CustomDatePicker
                    inputLabel={t('sessionStartDateTime')}
                    placeholder="DD/MM/YYYY - 00:00"
                    requiredTime
                    name="sessionStartDateTime"
                    value={calculatorData.sessionStartDateTime}
                    onChangeValue={(name, value) => updateCalculatorData(name, value)}
                    valueRequired
                  />
                </div>

                <div className="form-field">
                  <CustomDatePicker
                    inputLabel={t('sessionEndDateTime')}
                    placeholder="DD/MM/YYYY - 00:00"
                    requiredTime
                    name="sessionEndDateTime"
                    value={calculatorData.sessionEndDateTime}
                    onChangeValue={(name, value) => updateCalculatorData(name, value)}
                    valueRequired
                  />
                </div>

                <div className="form-field">
                  <CustomInput
                    inputLabel={t('powerConsumed')}
                    placeholder="Eg: 10"
                    name="powerConsumed"
                    type="text"
                    value={calculatorData.powerConsumed}
                    onChangeValue={(name, value) => updateCalculatorData(name, value)}
                    valueRequired
                  />
                </div>

                <div className="calculate-action">
                  <MyButton
                    label={t('calculate')}
                    buttonType="submit"
                    onClickFunc={handleCalculate}
                    disableButton={calculating}
                    loading={calculating}
                  />
                </div>

                <div className="price-breakdown-card">
                  <p className="price-breakdown-title">{t('priceBreakdown')}</p>

                  <div className="breakdown-table-header">
                    <span>{t('component')}</span>
                    <span>{t('amount')}</span>
                  </div>

                  <div className="breakdown-rows-list">
                    {calcResults.priceBreakdown?.map((comp: any, index: number) => (
                      <div className="breakdown-row-item" key={index}>
                        <span className="comp-name">{comp.componentName}</span>
                        <span className="comp-amt">{comp.amount}</span>
                      </div>
                    )) || null}
                  </div>

                  {timeSlotView && (
                    <>
                      <div className="breakdown-table-header pricing-slots-header">
                        <span>{t('pricingSlot')}</span>
                        <span>{t('amount')}</span>
                      </div>

                      <div className="breakdown-rows-list">
                        {calcResults.timeBasePrice?.map((comp: any, index: number) => (
                          <div className="breakdown-row-item" key={index}>
                            <span className="comp-name">{comp.componentName}</span>
                            <span className="comp-amt">{comp.amount}</span>
                          </div>
                        )) || null}
                      </div>
                    </>
                  )}

                  <div className="breakdown-total-footer">
                    <span className="footer-label">{t('totalAmount')}</span>
                    <span className="footer-value">
                      {parseFloat(calcResults.totalAmount).toFixed(2)} {calcResults.currency}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isSidePanel && (
        <div className="sidepanel-footer-actions">
          <MyButton
            label={t('verifyTariff')}
            buttonType="outline"
            onClickFunc={() => {
              navigate(`/ev-admin/tariffs/view/${tariffId}?isExtended=true`);
            }}
          />
          <MyButton
            label={t('editTariff')}
            buttonType="submit"
            onClickFunc={() =>
              navigate(
                `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
              )
            }
            disableButton={
              !permissionCheck('partnerTariffUpdate') && !permissionCheck('cpoTariffUpdate')
            }
          />
        </div>
      )}
    </div>
  );
}

ViewTariff.defaultProps = {
  id: '',
  isSidePanel: false,
};

export default ViewTariff;
